import { useParams } from "react-router";
import { useStationStore } from "../state/station-store";
import { useStationLoad } from "../hooks/useStationLoad";
import { useStationKeyboard } from "../hooks/useStationKeyboard";
import { useLiveDerivations } from "../hooks/useLiveDerivation";
import { StationHeader } from "./StationHeader";
import { StationContentValidateProvider } from "./StationContentValidateProvider";
import { TransactionList } from "./TransactionList";
import { TransactionPanes } from "./TransactionPanes";
import { TransactionPanel } from "./TransactionPanel";
import styles from "./RemittanceStation.module.css";

export function RemittanceStation() {
  const { batchId } = useParams<{ batchId: string }>();
  const { loading } = useStationLoad(batchId);
  const payload = useStationStore((s) => s.payload);
  const activeTransactionId = useStationStore((s) => s.activeTransactionId);
  const setActiveTransaction = useStationStore((s) => s.setActiveTransaction);
  // F-013 — derive through the hook (subscribes to payload + amountOverrides +
  // allocationDraft), NOT the store's stable liveBalance getter which wouldn't
  // re-render on keying pushes. One map, reused by the header Δ and rail chips.
  const derivations = useLiveDerivations();

  // Station-level shortcuts (Ctrl+Enter accept, PgUp/PgDn nav) — the Accept
  // button (rendered by TransactionPanel) owns the actual accept gate; this
  // just clicks it, so the disabled state naturally no-ops when not
  // acceptable and there's only ONE accept gate in the codebase.
  const idx = payload ? payload.transactions.findIndex((t) => t.id === activeTransactionId) : -1;
  useStationKeyboard({
    onAccept: () => { document.querySelector<HTMLButtonElement>(`.${styles.acceptBtn}`)?.click(); },
    onPrev: () => { if (payload && idx > 0) setActiveTransaction(payload.transactions[idx - 1]!.id); },
    onNext: () => { if (payload && idx >= 0 && idx < payload.transactions.length - 1) setActiveTransaction(payload.transactions[idx + 1]!.id); },
  });

  if (loading || !payload) return <div className={styles.loading}>Loading batch…</div>;

  // Batch rollup Δ = Σ live deltas over not-yet-accepted transactions.
  const batchDelta = payload.transactions.reduce((sum, t) => sum + (derivations.get(t.id)?.snapshot.deltaCents ?? 0), 0);
  const totalCount = payload.transactions.length;
  const acceptedCount = 0; // accepted transactions are removed from the rail (Task 14); rollup is over live ones
  const activeTransaction = payload.transactions.find((t) => t.id === activeTransactionId) ?? null;

  return (
    <div className={styles.station}>
      <StationHeader payload={payload} acceptedCount={acceptedCount} totalCount={totalCount} batchDeltaCents={batchDelta} />
      <StationContentValidateProvider>
        <div className={styles.body}>
          <TransactionList
            transactions={payload.transactions}
            activeId={activeTransactionId}
            verdictOf={(id) => derivations.get(id)?.snapshot.verdict ?? null}
            onSelect={setActiveTransaction}
          />
          {activeTransaction ? <TransactionPanes transaction={activeTransaction} /> : <main className={styles.center} data-testid="station-center" />}
          {activeTransaction ? <TransactionPanel transaction={activeTransaction} /> : <aside className={styles.right} data-testid="station-right" />}
        </div>
      </StationContentValidateProvider>
    </div>
  );
}
